import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Container, Typography, Chip, IconButton, Button } from '@mui/material';
import { FaCode, FaGithub, FaExternalLinkAlt, FaArrowRight, FaTimes, FaPlay } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import projects from '../data/projects';
import { trackProjectClick } from '../utils/analytics';

const monthIndex = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
const parseStartDate = (date) => {
  if (!date) return new Date(0);
  const match = date.match(/^([A-Za-z]+)\s+(\d{4})/);
  return match ? new Date(parseInt(match[2]), monthIndex[match[1]] ?? 0) : new Date(0);
};

function DrawerLabel({ children }) {
  return (
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#C4785D', mb: 1.25 }}>
      {children}
    </Typography>
  );
}

function ProjectDrawer({ project, onClose }) {
  useEffect(() => {
    if (!project) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [project, onClose]);

  useEffect(() => {
    document.body.style.overflow = project ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [project]);

  if (!project) return null;

  const { title, subtitle, date, description, technologies, github, demo, highlights } = project;

  const youtubeId = demo
    ? (demo.includes('youtube.com')
        ? demo.split('v=')[1]?.split('&')[0]
        : demo.includes('youtu.be')
          ? demo.split('youtu.be/')[1]?.split('?')[0]
          : null)
    : null;

  return createPortal(
    <AnimatePresence>
      {project && (
        <>
          {/* Backdrop */}
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1200,
              background: 'rgba(4, 3, 2, 0.78)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          />

          {/* Centering wrapper — pointer-events: none so backdrop click still works */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1201,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 0, sm: 2 },
              pointerEvents: 'none',
            }}
          >
            <motion.div
              key="modal-panel"
              initial={{ opacity: 0, scale: 0.93, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              style={{
                pointerEvents: 'auto',
                width: '100%',
                maxWidth: '1060px',
                maxHeight: 'min(720px, 92dvh)',
                background: '#1B1916',
                borderRadius: '14px',
                border: '1px solid rgba(234,227,214,0.09)',
                boxShadow: '0 40px 100px rgba(0,0,0,0.75), 0 0 0 1px rgba(234,227,214,0.04)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* ── Header ── */}
              <Box sx={{
                px: { xs: 2.5, md: 3.5 },
                py: { xs: 2, md: 2.25 },
                borderBottom: '1px solid rgba(234,227,214,0.07)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexShrink: 0,
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontFamily: "'Instrument Serif', serif",
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    color: '#EAE3D6',
                    lineHeight: 1.3,
                    fontWeight: 400,
                    mb: 0.9,
                    pr: 1,
                  }}>
                    {title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {subtitle && (
                      <Chip label={subtitle} size="small" sx={{ fontSize: '0.71rem', height: 22, backgroundColor: 'rgba(196,120,93,0.1)', color: '#C4785D', border: '1px solid rgba(196,120,93,0.2)' }} />
                    )}
                    {date && (
                      <Chip label={date} size="small" sx={{ fontSize: '0.71rem', height: 22, backgroundColor: 'rgba(234,227,214,0.05)', color: '#877D72', border: '1px solid rgba(234,227,214,0.08)' }} />
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ flexShrink: 0, color: '#524A42', '&:hover': { color: '#C4785D', backgroundColor: 'rgba(196,120,93,0.1)' } }}
                >
                  <FaTimes size={15} />
                </IconButton>
              </Box>

              {/* ── Two-column body ── */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flex: 1,
                overflow: 'hidden',
                minHeight: 0,
              }}>

                {/* LEFT: Video + GitHub + Technologies */}
                <Box sx={{
                  width: { xs: '100%', md: '46%' },
                  flexShrink: 0,
                  p: { xs: 2.5, md: 3 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.75,
                  borderRight: { md: '1px solid rgba(234,227,214,0.07)' },
                  borderBottom: { xs: '1px solid rgba(234,227,214,0.07)', md: 'none' },
                  overflowY: 'auto',
                }}>

                  {/* Video area */}
                  {demo && youtubeId ? (
                    /* Real YouTube embed */
                    <Box sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '16/9',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1px solid rgba(234,227,214,0.08)',
                      flexShrink: 0,
                    }}>
                      <iframe
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'block', border: 'none' }}
                        src={`https://www.youtube.com/embed/${youtubeId}`}
                        title={`${title} demo`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </Box>
                  ) : (
                    /* Cinematic placeholder */
                    <Box sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '16/9',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1px solid rgba(234,227,214,0.07)',
                      background: 'linear-gradient(145deg, #1C1814 0%, #231E18 55%, #2B2019 100%)',
                      flexShrink: 0,
                    }}>
                      {/* Warm radial glow */}
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(ellipse at 35% 48%, rgba(196,120,93,0.08) 0%, transparent 62%)',
                        pointerEvents: 'none',
                      }} />
                      {/* Film grain noise overlay */}
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0.025,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                        backgroundSize: '180px 180px',
                        pointerEvents: 'none',
                      }} />

                      {/* Centered content */}
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.75,
                      }}>
                        {/* Pulsing play ring */}
                        <Box
                          component={motion.div}
                          animate={{ opacity: [0.4, 0.85, 0.4], scale: [1, 1.04, 1] }}
                          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                          sx={{
                            width: 58,
                            height: 58,
                            borderRadius: '50%',
                            border: '1.5px solid rgba(234,227,214,0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(234,227,214,0.04)',
                          }}
                        >
                          <FaPlay size={19} color="#877D72" style={{ marginLeft: 3 }} />
                        </Box>

                        <Box sx={{ textAlign: 'center', px: 2 }}>
                          <Typography sx={{
                            fontFamily: "'Instrument Serif', serif",
                            fontStyle: 'italic',
                            fontSize: { xs: '1.05rem', md: '1.15rem' },
                            color: '#EAE3D6',
                            lineHeight: 1.3,
                            mb: 0.5,
                          }}>
                            Demo coming soon
                          </Typography>
                          <Typography sx={{ fontSize: '0.76rem', color: '#524A42', letterSpacing: '0.02em' }}>
                            A walkthrough video is on its way
                          </Typography>
                        </Box>
                      </Box>

                      {/* Bottom date tag */}
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2,
                        py: 1.1,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
                      }}>
                        {date && (
                          <Typography sx={{ fontSize: '0.67rem', color: 'rgba(234,227,214,0.28)', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                            {date}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Non-YouTube demo link */}
                  {demo && !youtubeId && (
                    <Button
                      href={demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                      startIcon={<FaExternalLinkAlt size={13} />}
                      onClick={() => trackProjectClick(title, 'Demo')}
                      sx={{
                        py: 1.35,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#C4C0B8',
                        border: '1px solid rgba(234,227,214,0.12)',
                        backgroundColor: 'rgba(234,227,214,0.04)',
                        borderRadius: '8px',
                        letterSpacing: '0.01em',
                        '&:hover': { backgroundColor: 'rgba(234,227,214,0.08)', border: '1px solid rgba(234,227,214,0.22)', color: '#EAE3D6' },
                      }}
                    >
                      Watch Demo
                    </Button>
                  )}

                  {/* Prominent GitHub button */}
                  {github ? (
                    <Button
                      href={github}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                      startIcon={<FaGithub size={17} />}
                      onClick={() => trackProjectClick(title, 'GitHub')}
                      sx={{
                        py: 1.45,
                        fontSize: '0.97rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#C4C0B8',
                        border: '1px solid rgba(196,120,93,0.28)',
                        backgroundColor: 'rgba(196,120,93,0.05)',
                        borderRadius: '8px',
                        letterSpacing: '0.01em',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(196,120,93,0.11)',
                          border: '1px solid rgba(196,120,93,0.5)',
                          color: '#EAE3D6',
                        },
                      }}
                    >
                      View on GitHub
                    </Button>
                  ) : (
                    <Button
                      disabled
                      fullWidth
                      startIcon={<FaGithub size={17} />}
                      sx={{
                        py: 1.45,
                        fontSize: '0.97rem',
                        textTransform: 'none',
                        color: '#3D3528 !important',
                        border: '1px solid rgba(234,227,214,0.07) !important',
                        borderRadius: '8px',
                      }}
                    >
                      Available on Request
                    </Button>
                  )}

                  {/* Technologies — below GitHub button */}
                  <Box>
                    <DrawerLabel>Technologies</DrawerLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65 }}>
                      {technologies.map((tech, i) => (
                        <Chip
                          key={i}
                          label={tech}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            backgroundColor: 'rgba(234,227,214,0.07)',
                            color: '#A8A09A',
                            border: '1px solid rgba(234,227,214,0.1)',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* RIGHT: Details panel (scrollable) — Overview + Highlights only */}
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: { xs: 2.5, md: 3 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.75,
                  minHeight: 0,
                }}>
                  <Box>
                    <DrawerLabel>Overview</DrawerLabel>
                    <Typography sx={{ fontSize: '0.94rem', fontWeight: 400, lineHeight: 1.9, color: '#C4C0B8' }}>
                      {description}
                    </Typography>
                  </Box>

                  {highlights && highlights.length > 0 && (
                    <Box>
                      <DrawerLabel>Highlights</DrawerLabel>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {highlights.map((h, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#C4785D', mt: '10px', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.91rem', lineHeight: 1.82, color: '#C4C0B8' }}>{h}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

              </Box>
            </motion.div>
          </Box>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

function FeaturedCard({ project, index, onOpen }) {
  const [hovered, setHovered] = useState(false);
  const num = String(index + 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.45, ease: 'easeOut' }}
      style={{ height: '100%' }}
    >
      <Box
        onClick={() => onOpen(project)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          p: { xs: 2.5, md: 3 },
          backgroundColor: hovered ? '#201D19' : '#1B1916',
          borderRadius: '10px',
          border: `1px solid ${hovered ? 'rgba(196,120,93,0.25)' : 'rgba(234,227,214,0.09)'}`,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        {/* Number */}
        <Typography sx={{
          fontSize: '0.82rem',
          color: '#C4785D',
          fontWeight: 600,
          letterSpacing: '0.02em',
          opacity: 0.7,
        }}>
          {num}
        </Typography>

        {/* Title */}
        <Typography sx={{
          fontFamily: "'Instrument Serif', serif",
          fontSize: { xs: '1.25rem', md: '1.4rem' },
          color: '#EAE3D6',
          lineHeight: 1.3,
          fontWeight: 400,
        }}>
          {project.title}
        </Typography>

        {/* Preview slot — cinematic placeholder, same language as the modal */}
        <Box sx={{
          position: 'relative',
          minHeight: 110,
          borderRadius: '7px',
          overflow: 'hidden',
          border: '1px solid rgba(234,227,214,0.07)',
          background: 'linear-gradient(145deg, #1C1814 0%, #231E18 55%, #2B2019 100%)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
        }}>
          <Box sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at 40% 50%, rgba(196,120,93,0.06) 0%, transparent 65%)',
            pointerEvents: 'none',
          }} />
          <Box
            component={motion.div}
            animate={{ opacity: [0.35, 0.7, 0.35] }}
            transition={{ repeat: Infinity, duration: 3.2, ease: 'easeInOut' }}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '1px solid rgba(234,227,214,0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FaPlay size={9} color="#6B6159" style={{ marginLeft: 2 }} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#6B6159', letterSpacing: '0.04em', userSelect: 'none', position: 'relative' }}>
            Demo coming soon
          </Typography>
        </Box>

        {/* Impact */}
        <Typography sx={{
          fontSize: '0.93rem',
          fontWeight: 400,
          color: '#B0A89E',
          lineHeight: 1.7,
          flex: 1,
        }}>
          {project.impact}
        </Typography>

        {/* Tech pills */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
          {project.technologies.slice(0, 5).map((tech, i) => (
            <Chip
              key={i}
              label={tech}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: 'rgba(234,227,214,0.06)',
                color: '#8A837C',
                border: '1px solid rgba(234,227,214,0.09)',
              }}
            />
          ))}
          {project.technologies.length > 5 && (
            <Chip
              label={`+${project.technologies.length - 5}`}
              size="small"
              sx={{ height: 24, fontSize: '0.75rem', backgroundColor: 'rgba(196,120,93,0.09)', color: '#C4785D' }}
            />
          )}
        </Box>

        {/* Footer: date + view */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.77rem', color: '#6B6159', letterSpacing: '0.01em' }}>
            {project.date}
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            color: hovered ? '#C4785D' : '#524A42',
            transition: 'color 0.2s, transform 0.2s',
            transform: hovered ? 'translateX(3px)' : 'none',
          }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, letterSpacing: '0.02em' }}>
              View project
            </Typography>
            <FaArrowRight size={10} />
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

function PastProjectRow({ project, index, onOpen }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, duration: 0.35, ease: 'easeOut' }}
    >
      <Box
        onClick={() => onOpen(project)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          py: 1.75,
          px: 1,
          mx: -1,
          borderBottom: '1px solid rgba(234,227,214,0.06)',
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'background-color 0.15s',
          backgroundColor: hovered ? 'rgba(234,227,214,0.025)' : 'transparent',
        }}
      >
        {/* Left: title + tech preview */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: { xs: '0.97rem', md: '1rem' },
            color: hovered ? '#EAE3D6' : '#C4C0B8',
            fontWeight: 500,
            transition: 'color 0.15s',
            lineHeight: 1.35,
          }}>
            {project.title}
          </Typography>
          <Typography sx={{ fontSize: '0.77rem', color: '#6B6159', mt: 0.35, lineHeight: 1 }}>
            {project.technologies.slice(0, 4).join(' · ')}
          </Typography>
        </Box>

        {/* Right: date + arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Typography sx={{
            fontSize: '0.78rem',
            color: '#6B6159',
            letterSpacing: '0.01em',
            display: { xs: 'none', sm: 'block' },
          }}>
            {project.date}
          </Typography>
          <Box sx={{
            color: hovered ? '#C4785D' : '#524A42',
            transition: 'color 0.15s, transform 0.15s',
            transform: hovered ? 'translateX(3px)' : 'none',
            display: 'flex',
            alignItems: 'center',
          }}>
            <FaArrowRight size={11} />
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

function SubLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#6B6159',
      mb: { xs: 1.75, md: 2 },
    }}>
      {children}
    </Typography>
  );
}

function ProjectsSection({ initialProjectId }) {
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    if (!initialProjectId) return;
    const project = projects.find(p => p.id === Number(initialProjectId));
    if (!project) return;
    const timer = setTimeout(() => {
      const el = document.getElementById('projects');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      setActiveProject(project);
    }, 400);
    return () => clearTimeout(timer);
  }, [initialProjectId]);

  if (!projects || projects.length === 0) return null;

  const featured = projects.filter(p => p.featured).sort((a, b) => parseStartDate(b.date) - parseStartDate(a.date));
  const past = projects.filter(p => !p.featured).sort((a, b) => parseStartDate(b.date) - parseStartDate(a.date));

  return (
    <>
      <Box id="projects" component="section" sx={{ py: { xs: 4, sm: 5.5, md: 7 }, background: 'rgba(20,18,16,0.45)' }}>
        <Container maxWidth="lg">

          {/* Section heading */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 3.5, md: 4.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 3, height: 22, backgroundColor: '#C4785D', borderRadius: 2, flexShrink: 0 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <FaCode size={16} color="#877D72" />
                  <Typography variant="h5" component="h2" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' }, color: '#EAE3D6', fontWeight: 600, letterSpacing: '-0.015em' }}>
                    Projects
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ fontSize: '0.87rem', color: '#524A42', fontWeight: 500 }}>
                {projects.length} projects
              </Typography>
            </Box>
          </motion.div>

          {/* Featured cards */}
          {featured.length > 0 && (
            <Box sx={{ mb: { xs: 4.5, md: 5.5 } }}>
              <SubLabel>Featured</SubLabel>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: { xs: 1.5, md: 2 },
                alignItems: 'stretch',
              }}>
                {featured.map((project, i) => (
                  <FeaturedCard key={project.id} project={project} index={i} onOpen={setActiveProject} />
                ))}
              </Box>
            </Box>
          )}

          {/* Past projects list */}
          {past.length > 0 && (
            <Box>
              <SubLabel>More Projects</SubLabel>
              <Box>
                {past.map((project, i) => (
                  <PastProjectRow key={project.id} project={project} index={i} onOpen={setActiveProject} />
                ))}
              </Box>
            </Box>
          )}

        </Container>
      </Box>

      <ProjectDrawer project={activeProject} onClose={() => setActiveProject(null)} />
    </>
  );
}

export default ProjectsSection;
